// YouTube API Service for Benipal Toys
// Note: Now uses backend API with database caching instead of direct YouTube API calls

import { apiService } from "./apiService";

export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  publishedAt: string;
  duration: string;
  viewCount: string;
  url: string;
  channelTitle: string;
}

export interface YouTubeChannelInfo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  subscriberCount: string;
  videoCount: string;
}

class YouTubeService {
  private cachedData: {
    channelInfo: YouTubeChannelInfo | null;
    videos: YouTubeVideo[];
  } | null = null;

  constructor() {
    // No longer needs API key or channel ID - handled by backend
  }

  async getChannelInfo(): Promise<YouTubeChannelInfo | null> {
    try {
      const response = await apiService.getYouTubeData();

      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to fetch YouTube data");
      }

      this.cachedData = {
        channelInfo: response.data.channelInfo,
        videos: response.data.videos || [],
      };

      return response.data.channelInfo;
    } catch (error) {
      console.error("Error fetching YouTube channel info:", error);
      throw error;
    }
  }

  async getVideos(maxResults: number = 12): Promise<YouTubeVideo[]> {
    try {
      // Use cached data if available, otherwise fetch
      if (!this.cachedData) {
        const response = await apiService.getYouTubeData();

        if (!response.success || !response.data) {
          throw new Error(response.error || "Failed to fetch YouTube data");
        }

        this.cachedData = {
          channelInfo: response.data.channelInfo,
          videos: response.data.videos || [],
        };
      }

      // Return limited results based on maxResults
      const videos = this.cachedData.videos || [];
      return videos.slice(0, maxResults);
    } catch (error) {
      console.error("Error fetching YouTube videos:", error);
      throw error;
    }
  }
}

export const youtubeService = new YouTubeService();
